import { useCallback, useEffect, useState } from 'react';
import { Button, Card, Col, Form, Modal, Nav, Row, Table } from 'react-bootstrap';
import PageHeader from '../components/common/PageHeader';
import Loader from '../components/common/Loader';
import EmptyState from '../components/common/EmptyState';
import ConfirmModal from '../components/common/ConfirmModal';
import TagInput from '../components/common/TagInput';
import { masterApi } from '../api';
import { useToast } from '../context/ToastContext';

const TYPES = ['Tablet', 'Capsule', 'Syrup', 'Injection', 'Drops', 'Ointment', 'Powder', 'Inhaler', 'Other'];
/** Same wording as the prescription form, so the two never drift apart. */
const TIMINGS = ['જમ્યા પછી', 'જમ્યા પહેલાં', 'સૂતા પહેલાં સાંજે', 'જમવાની સાથે', 'કોઈપણ સમયે'];

const EMPTY_MED = {
  name: '',
  type: 'Tablet',
  defaultFrequency: '1-0-1',
  defaultTiming: 'જમ્યા પછી',
  notes: '',
};

/** What a block of advice is filed under. */
const CATEGORIES = ['General', 'Obesity', 'Lifestyle Disease'];

const EMPTY_TPL = { title: '', category: 'General', items: [] };

export default function Masters() {
  const toast = useToast();
  const [tab, setTab] = useState('medicines');

  return (
    <>
      <PageHeader
        title="Medicines &amp; Advice Templates"
        subtitle="Fill these once so prescriptions can be written with a few clicks"
        icon="bi-capsule"
      />

      <Nav variant="tabs" activeKey={tab} onSelect={setTab} className="mb-3">
        <Nav.Item>
          <Nav.Link eventKey="medicines">
            <i className="bi bi-capsule me-1" /> Medicines
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="precautions">
            <i className="bi bi-shield-check me-1" /> Advice Templates
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {tab === 'medicines' ? <MedicineMaster toast={toast} /> : <PrecautionMaster toast={toast} />}
    </>
  );
}

function MedicineMaster({ toast }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await masterApi.medicines({ limit: 500 });
      setItems(res.data);
    } catch (err) {
      toast.error(err);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  async function save(e) {
    e.preventDefault();
    try {
      // Only how the medicine is prescribed is edited here - price and stock
      // belong to the inventory, and are deliberately not sent.
      const payload = {
        name: editing.name,
        type: editing.type,
        defaultFrequency: editing.defaultFrequency,
        defaultTiming: editing.defaultTiming,
      };

      if (editing._id) {
        await masterApi.updateMedicine(editing._id, payload);
        toast.success('Medicine updated');
      } else {
        await masterApi.createMedicine(payload);
        toast.success('Medicine added');
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err);
    }
  }

  const filtered = items.filter(
    (m) => !search.trim() || m.name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <>
      <Card>
        <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <Form.Control
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search medicines..."
            style={{ maxWidth: 280 }}
          />
          <Button className="btn-clinic" onClick={() => setEditing({ ...EMPTY_MED })}>
            <i className="bi bi-plus-lg me-1" /> Add Medicine
          </Button>
        </Card.Header>

        {loading ? (
          <Loader />
        ) : filtered.length === 0 ? (
          <EmptyState icon="bi-capsule" title="No medicines in the list" hint="Add the ones you prescribe most often." />
        ) : (
          <div className="table-responsive">
            <Table hover className="mb-0 align-middle" size="sm">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Form</th>
                  <th>Usual Prescription</th>
                  <th>Times Used</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => (
                  <tr key={m._id}>
                    <td className="fw-semibold">{m.name}</td>
                    <td className="text-muted small">{m.type}</td>
                    <td className="small">
                      {m.defaultFrequency} &middot; {m.defaultTiming}
                    </td>
                    <td className="text-muted small">{m.usageCount}</td>
                    <td className="text-end">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="btn-icon me-1"
                        onClick={() => setEditing({ ...EMPTY_MED, ...m })}
                      >
                        <i className="bi bi-pencil" />
                      </Button>
                      <Button size="sm" variant="outline-danger" className="btn-icon" onClick={() => setDeleting(m)}>
                        <i className="bi bi-trash" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card>

      <Modal show={!!editing} onHide={() => setEditing(null)} centered>
        {editing && (
          <Form onSubmit={save}>
            <Modal.Header closeButton>
              <Modal.Title className="h5">{editing._id ? 'Edit' : 'Add'} Medicine</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row className="g-3">
                <Col md={8}>
                  <Form.Label className="required">Name</Form.Label>
                  <Form.Control
                    required
                    autoFocus
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    placeholder="e.g. Paracetamol 500mg"
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Form</Form.Label>
                  <Form.Select value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })}>
                    {TYPES.map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={6}>
                  <Form.Label>Frequency</Form.Label>
                  <Form.Control
                    value={editing.defaultFrequency}
                    onChange={(e) => setEditing({ ...editing, defaultFrequency: e.target.value })}
                    placeholder="1-0-1"
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>When to take</Form.Label>
                  <Form.Select
                    value={editing.defaultTiming}
                    onChange={(e) => setEditing({ ...editing, defaultTiming: e.target.value })}
                  >
                    {TIMINGS.map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </Form.Select>
                </Col>

                <Col xs={12}>
                  <div className="section-hint">
                    <i className="bi bi-info-circle me-1" />
                    Price, pack size, stock and expiry live on the{' '}
                    <strong>Medicine Inventory</strong> page, so every change to them is recorded there.
                  </div>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="light" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-clinic">
                Save
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Modal>

      <ConfirmModal
        show={!!deleting}
        title="Remove this medicine?"
        body={deleting ? `"${deleting.name}" will stop appearing in the prescription suggestions. Past prescriptions are not affected.` : null}
        confirmLabel="Remove"
        onHide={() => setDeleting(null)}
        onConfirm={async () => {
          try {
            await masterApi.deleteMedicine(deleting._id);
            toast.success('Medicine removed');
            load();
          } catch (err) {
            toast.error(err);
          }
        }}
      />
    </>
  );
}

function PrecautionMaster({ toast }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await masterApi.precautions();
      setItems(res.data);
    } catch (err) {
      toast.error(err);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  async function save(e) {
    e.preventDefault();
    if (!editing.items.length) {
      toast.warn('Add at least one line of advice');
      return;
    }
    try {
      if (editing._id) {
        await masterApi.updatePrecaution(editing._id, editing);
        toast.success('Template updated');
      } else {
        await masterApi.createPrecaution(editing);
        toast.success('Template added');
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err);
    }
  }

  return (
    <>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>Pick a template while writing a prescription and all of its lines are added at once</span>
          <Button className="btn-clinic" onClick={() => setEditing({ ...EMPTY_TPL, items: [] })}>
            <i className="bi bi-plus-lg me-1" /> Add Template
          </Button>
        </Card.Header>

        {loading ? (
          <Loader />
        ) : items.length === 0 ? (
          <EmptyState icon="bi-shield-check" title="No templates yet" />
        ) : (
          <Card.Body>
            <Row className="g-3">
              {items.map((t) => (
                <Col md={6} key={t._id}>
                  <Card className="h-100">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <div className="fw-bold">{t.title}</div>
                          <div className="text-muted small">
                            {t.category} &middot; used {t.usageCount} times
                          </div>
                        </div>
                        <div className="d-flex gap-1">
                          <Button size="sm" variant="outline-secondary" className="btn-icon" onClick={() => setEditing({ ...t })}>
                            <i className="bi bi-pencil" />
                          </Button>
                          <Button size="sm" variant="outline-danger" className="btn-icon" onClick={() => setDeleting(t)}>
                            <i className="bi bi-trash" />
                          </Button>
                        </div>
                      </div>
                      <ol className="small mt-2 mb-0 ps-3 text-muted">
                        {t.items.map((line, i) => (
                          <li key={i}>{line}</li>
                        ))}
                      </ol>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card.Body>
        )}
      </Card>

      <Modal show={!!editing} onHide={() => setEditing(null)} centered size="lg">
        {editing && (
          <Form onSubmit={save}>
            <Modal.Header closeButton>
              <Modal.Title className="h5">{editing._id ? 'Edit' : 'Add'} Advice Template</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row className="g-3">
                <Col md={8}>
                  <Form.Label className="required">Title</Form.Label>
                  <Form.Control
                    required
                    autoFocus
                    value={editing.title}
                    onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                    placeholder="e.g. Diabetes"
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Category</Form.Label>
                  <Form.Select
                    value={editing.category}
                    onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  >
                    {/* An older template may sit in a category no longer on the
                        list - keep it rather than silently move it. */}
                    {!CATEGORIES.includes(editing.category) && editing.category && (
                      <option>{editing.category}</option>
                    )}
                    {CATEGORIES.map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xs={12}>
                  <Form.Label className="required">Advice lines</Form.Label>
                  <TagInput
                    value={editing.items}
                    onChange={(items2) => setEditing({ ...editing, items: items2 })}
                    placeholder="Type one instruction and press Enter"
                  />
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="light" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-clinic">
                Save
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Modal>

      <ConfirmModal
        show={!!deleting}
        title="Remove this template?"
        body={deleting ? `"${deleting.title}" will no longer appear in the template list.` : null}
        confirmLabel="Remove"
        onHide={() => setDeleting(null)}
        onConfirm={async () => {
          try {
            await masterApi.deletePrecaution(deleting._id);
            toast.success('Template removed');
            load();
          } catch (err) {
            toast.error(err);
          }
        }}
      />
    </>
  );
}
